import { NavLink, Outlet } from "react-router";
import { useTranslation } from "react-i18next";
import { cx } from "../../components/ui";
import { useAppTabContext } from "./context";

/*
 * Releases and runtime logs, under one tab.
 *
 * They were two, and the split asked the customer to know which kind of
 * output they wanted before they had seen either: a build that failed and a
 * process that crashed on boot are the same question -- "what happened" --
 * answered by two different streams. Anybody debugging opened both and moved
 * between them, which is what a sub-nav is for and a top-level tab is not.
 *
 * `/logs` still resolves, and lands here: an app's log URL is the one people
 * paste into a chat at three in the morning, and breaking it to tidy a nav
 * would be a poor trade.
 *
 * The nav is a segmented control rather than a second row of underlined
 * tabs, because two rows of the same idiom make a reader work out which is
 * the page and which is the view.
 */
const VIEWS: { to: string; key: "releases" | "logs"; end?: boolean }[] = [
  { to: ".", key: "releases", end: true },
  { to: "logs", key: "logs" },
];

export function ActivityTab() {
  const { t } = useTranslation("app");
  /*
   * Forwarded, not re-derived. This route sits between the page that owns the
   * app and the views that read it, and an `<Outlet />` with no context of its
   * own hands children `undefined` -- which is not a type error anywhere,
   * because `useOutletContext` is generic, and shows up as both views
   * crashing on their first destructure.
   */
  const context = useAppTabContext();

  return (
    <div className="space-y-4">
      <div role="tablist" className="inline-flex rounded-md border border-line p-0.5">
        {VIEWS.map((view) => (
          <NavLink
            key={view.key}
            to={view.to}
            end={view.end}
            role="tab"
            className={({ isActive }) =>
              cx(
                "rounded-[3px] px-3 py-1 text-[13px] font-medium transition-colors duration-200 ease-smooth focus-visible:ring-2 focus-visible:ring-accent/60 focus-visible:outline-none",
                isActive ? "bg-inverse text-on-inverse" : "text-fg-muted hover:text-fg",
              )
            }
          >
            {t(`activity.${view.key}`)}
          </NavLink>
        ))}
      </div>

      <Outlet context={context} />
    </div>
  );
}
